import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IconFileUpload, IconFileTypeXml } from "@tabler/icons-react";
import { ShopifyGlyph } from "campaign-builder/ui/icons/ShopifyGlyph";
import CONFIG from "config/config";
import { getAllClientConnectedIntegrations } from "module/integrations/services";
import { INTEGRATIONS, type Integration } from "module/integrations/dto";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";

interface OptionTileProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
}

const OptionTile = ({ icon, title, description, actionLabel, onAction }: OptionTileProps) => (
  <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
    <div className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-foreground">{icon}</div>
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <p className="text-foreground text-sm font-semibold leading-5">{title}</p>
      <p className="text-muted-foreground text-xs leading-4">{description}</p>
    </div>
    <button
      type="button"
      onClick={onAction}
      className="text-foreground h-9 shrink-0 rounded-lg border border-solid border-border bg-background px-4 text-sm font-medium"
    >
      {actionLabel}
    </button>
  </div>
);

export const AudienceSourceTiles = () => {
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { setIsRedirectingToAudience } = useCampaignData();
  const [hasShopifyIntegration, setHasShopifyIntegration] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let active = true;
    getAllClientConnectedIntegrations(Number(clientId))
      .then((integrations: Integration[]) => {
        if (!active) return;
        setHasShopifyIntegration(integrations.some((integration) => integration.provider === INTEGRATIONS.SHOPIFY));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [clientId]);

  const goToAudiences = (extraParams = "") => {
    setIsRedirectingToAudience(true);
    window.location.href = `${CONFIG.NEXT_BASE_URL}/dashboard/${clientId}/av/audiences?campaignId=${campaignId}${extraParams}`;
  };

  const goToShopify = () => {
    if (hasShopifyIntegration) {
      goToAudiences("&templatesPage=true");
      return;
    }
    setIsRedirectingToAudience(true);
    window.location.href = CONFIG.SHOPIFY_BASE_URL || `${CONFIG.NEXT_BASE_URL}/dashboard/${clientId}/integrations`;
  };

  return (
    <div className="flex flex-col gap-2">
      <OptionTile
        icon={<IconFileUpload size={18} stroke={1.5} />}
        title="Upload audience file"
        description="Upload your audience list in either .CSV, .XLSX"
        actionLabel="Upload file"
        onAction={() => goToAudiences()}
      />
      <OptionTile
        icon={<IconFileTypeXml size={18} stroke={1.5} />}
        title="Upload .KML file"
        description="Create geographic segmentations using map files (KML)"
        actionLabel="Upload KML file"
        onAction={() => goToAudiences("&fileSource=kml")}
      />
      <div className="flex items-center gap-3">
        <div className="bg-slate-200 dark:bg-accent h-px flex-1" />
        <span className="text-muted-foreground text-xs font-medium">OR</span>
        <div className="bg-slate-200 dark:bg-accent h-px flex-1" />
      </div>
      <OptionTile
        icon={<ShopifyGlyph className="h-[18px] w-[18px]" />}
        title="Shopify"
        description="Connect your store to sync data for audiences and campaigns"
        actionLabel={hasShopifyIntegration ? "Create segment" : "Connect to Shopify"}
        onAction={goToShopify}
      />
    </div>
  );
};
